/**
 * Phase 1 Gate-0 entitlement helpers (mirrors backend/src/config/entitlements.js).
 * Permissions = UNION of all Keycloak realm roles on the user.
 */

export const ACCESS = {
  NONE: "none",
  VIEW: "view",
  WRITE: "write",
};

export const MODULES = {
  GATE0_ONBOARD: "gate0.onboard",
  GATE0_CONFIG: "gate0.config",
  GATE0_CHAT: "gate0.chat",
  GATE0_SUMMARY: "gate0.summary",
};

export const TENANT_ROLES = {
  TENANT_ADMIN: "tenant-admin",
  ENTERPRISE_AI_OWNER: "enterprise-ai-owner",
  TEVV_LEAD: "tevv-lead",
  RISK_REVIEWER: "risk-reviewer",
  SECURITY_REVIEWER: "security-reviewer",
  HEAD_OF_QUALITY: "head-of-quality",
};

export const ALL_TENANT_ROLES = Object.values(TENANT_ROLES);

export const PLATFORM_ADMIN = "platform-admin";

const V = ACCESS.VIEW;
const W = ACCESS.WRITE;
const N = ACCESS.NONE;

const ENTITLEMENT_MATRIX = {
  [TENANT_ROLES.TENANT_ADMIN]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [TENANT_ROLES.ENTERPRISE_AI_OWNER]: {
    [MODULES.GATE0_ONBOARD]: W,
    [MODULES.GATE0_CONFIG]: W,
    [MODULES.GATE0_CHAT]: W,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [TENANT_ROLES.TEVV_LEAD]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [TENANT_ROLES.RISK_REVIEWER]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [TENANT_ROLES.SECURITY_REVIEWER]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [TENANT_ROLES.HEAD_OF_QUALITY]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
  [PLATFORM_ADMIN]: {
    [MODULES.GATE0_ONBOARD]: N,
    [MODULES.GATE0_CONFIG]: N,
    [MODULES.GATE0_CHAT]: N,
    [MODULES.GATE0_SUMMARY]: V,
  },
};

const RANK = { [ACCESS.NONE]: 0, [ACCESS.VIEW]: 1, [ACCESS.WRITE]: 2 };

export function accessFor(roles, module) {
  let best = ACCESS.NONE;
  for (const role of roles || []) {
    const level = ENTITLEMENT_MATRIX[role]?.[module] || ACCESS.NONE;
    if (RANK[level] > RANK[best]) best = level;
  }
  return best;
}

export function can(roles, module, action = ACCESS.VIEW) {
  const level = accessFor(roles, module);
  if (action === ACCESS.WRITE) return level === ACCESS.WRITE;
  if (action === ACCESS.VIEW) return RANK[level] >= RANK[ACCESS.VIEW];
  return false;
}

export function canOnboard(roles) {
  return can(roles, MODULES.GATE0_ONBOARD, ACCESS.WRITE);
}

export function canConfigure(roles) {
  return can(roles, MODULES.GATE0_CONFIG, ACCESS.WRITE);
}

export function canChatWrite(roles) {
  return can(roles, MODULES.GATE0_CHAT, ACCESS.WRITE);
}

export function canChatView(roles) {
  return can(roles, MODULES.GATE0_CHAT, ACCESS.VIEW);
}

export function canViewSummary(roles) {
  return can(roles, MODULES.GATE0_SUMMARY, ACCESS.VIEW);
}

export function isEnterpriseAiOwner(roles) {
  return (roles || []).includes(TENANT_ROLES.ENTERPRISE_AI_OWNER);
}

export function isTenantAdmin(roles) {
  return (roles || []).includes(TENANT_ROLES.TENANT_ADMIN);
}

export function isPlatformAdmin(roles) {
  return (roles || []).includes(PLATFORM_ADMIN);
}

/** Default post-login home for tenant users */
export function defaultAppPath(roles) {
  if (isPlatformAdmin(roles)) return "/platform/tenants";
  // tenant-admin lands on the dedicated client-admin console (/client/*), not the
  // general team-members page.
  if (isTenantAdmin(roles)) return "/client/overview";
  // EAO lands on app portfolio (/home) where Onboard App lives — not client admin console
  return "/home";
}
